import { makeAutoObservable, observable, reaction } from 'mobx';

import { getBalance } from '@/lib/api/auth';
import { getDisciplinesViews } from '@/helpers/getDisciplinesViews';
import { LS } from '@/helpers/localStorageHelper';
import { timeout } from '@/helpers/timeout';
import { defaultSortingByTournaments } from '@/config/constants';

const AuthError = observable({
  code: null,
  description: '',
  meta: {
    time: null,
  },
});

const UserData = {
  address: '',
  birthday: null,
  city: '',
  country: '',
  currencyId: null,
  email: '',
  favourites: [],
  firstName: '',
  gender: '',
  id: null,
  intercomHash: null,
  isDocsVerified: false,
  isEmailVerified: true,
  isRulesConfirmed: false,
  isTest: false,
  lastName: '',
  phoneNumber: '',
  postCode: '',
  selfExcludedEnd: null,
  selfExcludedStart: null,
  steamId: '',
  verification: 0,
};

export class Auth {
  constructor(root, initialState) {
    makeAutoObservable(this);

    this.root = root;
    Object.assign(this, initialState);

    reaction(
      () => this.demo,
      (demo) => {
        const app =
          this.root.ui.appRoot.getElementById('app-container') ||
          this.root.ui.appRoot.getElementById('app');
        if (demo) {
          app.classList.remove('app');
          app.classList.add('demo-app');
        } else {
          app.classList.remove('demo-app');
          app.classList.add('app');
        }
      },
    );

    reaction(
      () => this.brand,
      (brand) => {
        this.root.ui.setDisciplinesViews(brand);
      },
    );
  }

  /** @type {string | null} */
  token = '';
  tokenChecked = false;
  brand = '';

  /** @type {string | null} */
  subPartnerId = null;
  demo = false;
  userData = UserData;
  country = '';
  demoSingUpProcess = false;
  errors = {
    joinError: AuthError,
    loginError: AuthError,
    recoveryError: AuthError,
    renewError: AuthError,
    resendError: AuthError,
  };
  isLogged = false;
  hasBrand = false;

  /**
   * Flag to re-initialize the auth state in 'AuthResolver' component
   *
   * @type {boolean}
   */
  isReInit = false;

  /**
   * Sets the flag to re-initialize the auth state.
   *
   * @param {boolean} bool
   */
  setIsReInit = (bool = false) => {
    this.tokenChecked = !bool;
    this.isReInit = bool;
  };

  /**
   * An initial method to set user's creds for further usage for authenticated requests
   *
   * @param {object} params
   * @param {string} params.cid
   * @param {string|null=} params.token
   */
  setCredentials = ({ cid, token }) => {
    this.setBrand(cid);
    this.setToken(token);
  };

  /**
   * @private
   *
   * @param {string | null=} token
   */
  setToken = (token = null) => {
    this.token = token || null;
    this.isLogged = !!this.token;

    if (this.token) {
      LS.set('token', token);
    } else {
      LS.remove('token');
    }
  };

  setTokenChecked = (tokenChecked = true) => {
    this.tokenChecked = tokenChecked;
  };

  /**
   * @param {string | null} subPartnerId
   */
  setSubPartnerId = (subPartnerId) => {
    this.subPartnerId = subPartnerId;
  };

  /**
   * @private
   *
   * @param {string | null=} brand
   */
  setBrand = (brand = null) => {
    // we can not set brand as `null`
    // because some part of code relies on it to always be a string
    this.brand = brand || '';

    if (brand) {
      LS.set('brand', brand);
    } else {
      LS.remove('brand');
    }
  };

  setHasBrand = (hasBrand = true) => {
    this.hasBrand = hasBrand;
  };

  /**
   * @param {boolean} demo
   */
  setDemo = (demo) => {
    this.demo = demo;

    if (demo) {
      LS.set('demo', demo);
    } else {
      LS.remove('demo');
    }
  };

  isFirstRequestTokenChecker = true;

  runTokenChecker = async () => {
    if (!this.token) {
      return;
    }

    if (!this.isFirstRequestTokenChecker) {
      await timeout(60);
    }

    // In case we were performed logout during the timeout
    if (!this.token) {
      return;
    }

    this.isFirstRequestTokenChecker = false;

    if (!this.root.ui.showBalance) {
      this.runTokenChecker();
      return;
    }

    try {
      const { profile } = this.root;
      const { currency, user } = profile;

      const { data } = await getBalance({
        apiKey: this.brand,
        currency: currency.name,
        profileId: user.id,
        token: this.token,
      });
      if (data) {
        profile.setBalance(data);
        this.runTokenChecker();
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 409) {
          this.runTokenChecker();
        }
      }
    }
  };

  logout = () => {
    LS.remove('settledExpressPresets');

    this.setDemo(false);
    this.setToken(null);
    this.setBrand(null);
    this.root.profile.setBalance(null);
  };
}
